import { useState, useEffect, createContext, useContext } from 'react';
import { User } from '../types';
import { authService, handleApiError } from '../services';
import { trackEvent, setUserId } from '../services/analytics';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  // recaptchaToken is the v2 widget response. Backend rejects the
  // login when RECAPTCHA_SECRET_KEY is set on the server and this token
  // is missing or fails Google's siteverify; optional locally during
  // development.
  login: (email: string, password: string, recaptchaToken?: string) => Promise<void>;
  logout: () => void;
  register: (userData: RegisterData) => Promise<void>;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  companyName: string;
  phone: string;
  // Mowilex-specific fields
  mowilexCustomerId?: string;
  territory: string;
  contactPerson: string;
  // Indonesian address fields
  address1: string;
  address2?: string;
  city: string;
  province: string;
  kecamatan: string;
  kelurahan: string;
  postalCode: string;
  // Legacy address field for backward compatibility
  address?: string;
  // reCAPTCHA v2 widget token. Passed through to /auth/register; the
  // backend rejects when RECAPTCHA_SECRET_KEY is set and this token
  // is missing or fails Google's siteverify.
  recaptchaToken?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useAuthProvider = (): AuthContextType => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          // Fetch user profile from API
          const userProfile = await authService.getUserProfile();

          // Map API response to User interface
          const mappedUser: User = {
            id: userProfile.id,
            email: userProfile.email,
            name: userProfile.full_name,
            full_name: userProfile.full_name,
            role: userProfile.role as any, // Type assertion for role mapping
            customerId: userProfile.epicor_customer_id || undefined,
            customer_id: userProfile.epicor_customer_id,
            isVerified: userProfile.is_verified,
            is_verified: userProfile.is_verified,
            language: 'id', // Default language
          };

          setUser(mappedUser);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear invalid auth state
        authService.logout();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Proactive token refresh: refresh 2 minutes before access token expires
  useEffect(() => {
    if (!user) return;

    const scheduleRefresh = () => {
      const expiry = localStorage.getItem('token_expiry');
      if (!expiry) return undefined;

      const expiryTime = parseInt(expiry, 10);
      const refreshAt = expiryTime - 2 * 60 * 1000; // 2 minutes before expiry
      const delay = refreshAt - Date.now();

      if (delay <= 0) {
        // Already near expiry, refresh now
        authService.refreshToken().then(scheduleRefresh).catch(() => {
          authService.logout();
          setUser(null);
        });
        return undefined;
      }

      return window.setTimeout(() => {
        authService.refreshToken().then(scheduleRefresh).catch(() => {
          authService.logout();
          setUser(null);
        });
      }, delay);
    };

    const timerId = scheduleRefresh();
    return () => {
      if (timerId !== undefined) clearTimeout(timerId);
    };
  }, [user]);

  const login = async (email: string, password: string, recaptchaToken?: string) => {
    setIsLoading(true);
    try {
      // Use real API service
      const loginResponse = await authService.login({
        email,
        password,
        recaptcha_token: recaptchaToken,
      });
      
      // Map API response to User interface
      const mappedUser: User = {
        id: loginResponse.user.id,
        email: loginResponse.user.email,
        name: loginResponse.user.full_name,
        full_name: loginResponse.user.full_name,
        role: loginResponse.user.role as any, // Type assertion for role mapping
        customerId: loginResponse.user.customer_id || undefined,
        customer_id: loginResponse.user.customer_id,
        isVerified: loginResponse.user.is_verified,
        is_verified: loginResponse.user.is_verified,
        language: 'id', // Default language
      };

      setUser(mappedUser);
      // Analytics: successful login. setUserId uses the portal UUID
      // (not email) so we can trace repeat visits without PII in GA.
      setUserId(mappedUser.id);
      trackEvent('login_success', { role: mappedUser.role });
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      trackEvent('login_failed', { reason: errorMessage });
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    // Clear the userId so subsequent anonymous browsing isn't
    // attributed to the previous user.
    setUserId(null);
  };

  const register = async (userData: RegisterData) => {
    setIsLoading(true);
    try {
      // Map frontend RegisterData to API RegisterRequest
      // Generate combined address for legacy compatibility
      const combinedAddress = [
        userData.address1,
        userData.address2,
        userData.city,
        userData.kecamatan,
        userData.kelurahan,
        userData.province,
        userData.postalCode
      ].filter(Boolean).join(', ');

      const apiRegisterData = {
        email: userData.email,
        password: userData.password,
        full_name: userData.name,
        company_name: userData.companyName,
        contact_person: userData.contactPerson,
        phone: userData.phone,
        // Mowilex-specific fields
        mowilex_customer_id: userData.mowilexCustomerId || undefined,
        territory: userData.territory,
        // Indonesian address fields
        address1: userData.address1,
        address2: userData.address2 || undefined,
        city: userData.city,
        province: userData.province,
        kecamatan: userData.kecamatan,
        kelurahan: userData.kelurahan,
        postal_code: userData.postalCode,
        // Legacy address field for backward compatibility
        address: userData.address || combinedAddress,
        // reCAPTCHA token. Both fields are sent so the backend's
        // legacy /signup-request handler (which still reads
        // captcha_token) and the new /auth/register handler (which
        // reads recaptcha_token) both work without further changes.
        captcha_token: userData.recaptchaToken,
        recaptcha_token: userData.recaptchaToken,
      };
      
      await authService.register(apiRegisterData);
      
      // Don't auto-login after registration - user needs admin verification
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    user,
    isLoading,
    login,
    logout,
    register,
  };
};

export { AuthContext };